import type Phaser from "phaser";
import { MissingFrameError, SOLID_ALPHA, type AssetManifest, type FrameMask, type FrameMaskOf } from "daedalus-core";
import { pageTextureKey } from "./atlas.ts";

/**
 * The solid pixels of atlas frames, for the occluder fade (ADR-040): the first time a frame is asked for,
 * its rect of its page texture's source image is drawn to a 2D canvas, its alpha read against
 * `SOLID_ALPHA` and the mask kept by frame id, so asking again allocates nothing. One instance serves every
 * prop view of a scene, so a frame placed on several layers is read once. Page images reach Phaser's
 * loader through XHR as same-origin blobs, so reading them back is allowed wherever WebGL may draw them.
 * The browser decodes a page for 2D drawing only when it is first read back, which costs a whole frame for
 * a large page, so `prepare` reads a pixel of each page while the world loads. A game page holds only some of the
 * manifest's pages at a time: `prepare` readies the pages given, and `release` forgets the masks of pages that are let
 * go of, so the masks held are those of the pages held.
 */
export class SolidMasks {
  private readonly masks = new Map<string, FrameMask>();
  private context: CanvasRenderingContext2D | null = null;

  /**
   * @param textures The scene's texture manager, holding the pages the masks are read from (`registerAtlasPage`).
   * @param manifest Asset manifest.
   */
  public constructor(
    private readonly textures: Phaser.Textures.TextureManager,
    private readonly manifest: AssetManifest,
  ) {}

  /**
   * Number of frames read so far.
   *
   * @returns The count.
   */
  public get count(): number {
    return this.masks.size;
  }

  /**
   * Readies every page for reading back by drawing and reading one pixel of each, so the first mask read of
   * a page does not stall a frame while the browser decodes the page for 2D drawing (about 18 ms for a 2048
   * by 2048 page). Call once after the pages are registered, while the world loads; no mask is read.
   *
   * @param pages The page indices to ready: the pages just registered when the game page holds only some of the
   *   manifest's; every page of the manifest when omitted.
   * @throws Error when a page is not a loaded texture or cannot be read back.
   */
  public prepare(pages: Iterable<number> = this.manifest.pages.keys()): void {
    for (const page of pages) {
      this.pixels(page, { x: 0, y: 0, w: 1, h: 1 }, `page ${page}`);
    }
  }

  /**
   * Forgets the masks read from pages the page lets go of, so a page dropped from the texture manager leaves nothing of
   * its frames behind. A frame of a page that is loaded again is read afresh.
   *
   * @param pages The page indices let go of.
   */
  public release(pages: Iterable<number>): void {
    const dropped = new Set(pages);
    for (const frame of [...this.masks.keys()]) {
      if (dropped.has(this.manifest.frames[frame]?.page as number)) {
        this.masks.delete(frame);
      }
    }
  }

  /**
   * The solid pixels of a frame, read from its atlas page the first time.
   *
   * @param frame The frame id.
   * @returns The mask.
   * @throws MissingFrameError when the manifest has no such frame.
   * @throws Error when its page is not a loaded texture, or the page's image cannot be read.
   */
  public readonly maskOf: FrameMaskOf = (frame) => {
    const known = this.masks.get(frame);
    if (known !== undefined) {
      return known;
    }
    const rect = this.manifest.frames[frame];
    if (rect === undefined) {
      throw new MissingFrameError(frame);
    }
    const pixels = this.pixels(rect.page, rect, `frame ${frame}`);
    const solid = new Uint8Array(rect.w * rect.h);
    for (let i = 0; i < solid.length; i += 1) {
      solid[i] = (pixels[i * 4 + 3] as number) >= SOLID_ALPHA ? 1 : 0;
    }
    const mask = { w: rect.w, h: rect.h, solid };
    this.masks.set(frame, mask);
    return mask;
  };

  /**
   * Draws a rectangle of a page's source image to the canvas and reads its pixels back.
   *
   * @param page The page index.
   * @param rect The rectangle on the page.
   * @param what What is read, for messages.
   * @returns The rectangle's RGBA pixels, row-major.
   * @throws Error when the page is not a loaded texture, is not an image, or cannot be read back.
   */
  private pixels(page: number, rect: { readonly x: number; readonly y: number; readonly w: number; readonly h: number }, what: string): Uint8ClampedArray {
    const key = pageTextureKey(this.manifest, page);
    if (!this.textures.exists(key)) {
      throw new Error(`atlas page ${key} is not a texture, so ${what} has no pixels to read`);
    }
    const source = this.textures.get(key).getSourceImage();
    if (!(source instanceof HTMLImageElement || source instanceof HTMLCanvasElement || source instanceof ImageBitmap)) {
      throw new Error(`atlas page ${key} is not an image, so ${what} has no pixels to read`);
    }
    const context = this.canvas();
    context.canvas.width = rect.w;
    context.canvas.height = rect.h;
    context.drawImage(source, rect.x, rect.y, rect.w, rect.h, 0, 0, rect.w, rect.h);
    try {
      return context.getImageData(0, 0, rect.w, rect.h).data;
    } catch (error) {
      throw new Error(`atlas page ${key} cannot be read back, so ${what} has no pixels`, { cause: error });
    }
  }

  /**
   * The 2D context frames are drawn into, made on first use.
   *
   * @returns The context.
   * @throws Error when the browser offers no 2D canvas.
   */
  private canvas(): CanvasRenderingContext2D {
    if (this.context === null) {
      const context = document.createElement("canvas").getContext("2d", { willReadFrequently: true });
      if (context === null) {
        throw new Error("no 2D canvas is available to read frame masks");
      }
      this.context = context;
    }
    return this.context;
  }
}
